// Rogers' screen 04 (discovery tracker) — "watch your guarantee form". Ported from the reference
// app's forming time series and three-bar waterfall — datanac-core-dbx/products/
// mco_eco_app_reference/js/tracker.js, drawTS() and drawWF(). That source is the spec, and its
// shape is deliberate:
//
//   * ONE y axis for all three series. Input cost is the SHADED BAND between the price and margin
//     lines, not a fourth line: the cost is the vertical gap, so drawing it as the gap is the one
//     rendering that can't misread. It keeps its own line too, but only when it happens to fall
//     inside the zoomed domain (per bushel it does; per acre it sits far below and is dropped).
//   * The axis is zoomed to min(margin) / max(price) plus padding, never zero-based — the whole
//     point is the daily drift.
//   * The scrubbed day carries end-of-line labels ("price $10.73" / "costs $1.30" /
//     "margin $9.43") so the three values read off the chart without a tooltip, and the waterfall
//     below re-states the same day as price − costs = margin.
//   * Crop / practice / year are NOT selectors here: the dialog title carries that context, which
//     the Scenario page's route params already fixed.
//   * Past the newest settled day the three lines continue DASHED and flat to the window's close.
//     Nothing forecasts them — proj_* rows exist only for days that have already settled — so the
//     dashed run holds the last settled value and says so in its tooltip.
//   * Units follow the county yield. RMA publishes no expected area yield until ~3 business days
//     AFTER the window closes (app_implementation_guide.md §3), so in-window dollars per acre rest
//     on our yield estimate — disclosed under the chart. Multiplying all three series through the
//     bridge preserves the shape and the reconciliation, so the single axis still holds. With no
//     yield at all it falls back to per bushel, which is exact and yield-free from day one.
//
// x is windowed to the discovery period (see discoveryWindow) rather than auto-fit, so days
// settled and days remaining read off the axis.

import { useEffect, useMemo, useRef, useState } from "react";

import { useTheme } from "@emotion/react";
import {
  Alert,
  Box,
  Card,
  Chip,
  Slider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  allocateCents,
  bridgeToPerAcre,
  costPerAcre,
  daysToClose,
  discoveryWindow,
  paddedDomain,
  revenuePerAcre,
  withinWindow,
} from "api-toolkit/mco/mcoTracker";
import { formatPrice } from "utility/formatters";
import vegaEmbed from "vega-embed";

// The reference app's palette: blue is the crop price, green the margin, orange the cost. Trigger
// and market-implied are not in the source series — they get hues no other line uses so the legend
// stays readable. Trigger is the one amber near the cost orange; its dash pattern separates them.
const COLOR_PRICE = "#4a7ba7";
const COLOR_MARGIN = "#3f8462";
const COLOR_COST = "#a9632f";
const COLOR_BAND = "rgba(169, 99, 47, 0.2)";
const COLOR_TRIGGER = "#c9971f";
const COLOR_MARKET = "#6b4fa0";
const COLOR_GUIDE = "#8a929b";

// leg key -> [display name, the Official PriceDiscovery leg it matches, its native price unit].
// Order is the ledger's order; the basket decides which of them actually appear.
const LEG_META = {
  urea: ["Urea", "Urea", "/ton"],
  dap: ["DAP", "DAP", "/ton"],
  potash: ["Potash", "Potash", "/ton"],
  naturalGas: ["Natural gas", "Natural Gas", "/MMBtu"],
  diesel: ["Diesel, per bushel", "Diesel", "/gal"],
};
const LEG_ORDER = ["urea", "dap", "potash", "naturalGas", "diesel"];
// The reference app's cost ramp — one hue family so the strip reads as one basket.
const LEG_COLOR = {
  urea: "#a9632f",
  dap: "#c08147",
  potash: "#d2a067",
  naturalGas: "#e3c193",
  diesel: "#b8a98c",
};

const BASIS_LABEL = { forming: "forming", locked: "locked", official: "official" };
// The same three states in agent language, for the readout badge.
const BASIS_PHRASE = { official: "RMA published it", locked: "set, can't change", forming: "still moving" };

const priceOrPending = (v, places = 4) => (v == null ? "not yet determined" : formatPrice(v, places));

/** Money at 2dp — the visual labels all follow the design's cents, whatever the tooltips carry. */
const f2 = (v) => formatPrice(v, 2);

/** "+$0.03" / "−$0.03" / "flat" against day one. Sub-cent moves read as flat, never as "+$0.00". */
function drift(now, base) {
  if (now == null || base == null) return "";
  const d = Math.round((now - base) * 100) / 100;
  return Math.abs(d) < 0.005 ? "flat" : `${d > 0 ? "+" : "−"}${f2(Math.abs(d))}`;
}

const MONTH_DAY = { month: "short", day: "2-digit" };

function formatDateShort(iso) {
  return iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", MONTH_DAY) : "";
}

const formatMs = (ms) => new Date(ms).toLocaleDateString("en-US", MONTH_DAY);

export default function McoTrackerChart({ tracker, forecastPaymentPerAcre = null, priceLegs = [] }) {
  const theme = useTheme();
  const chartRef = useRef(null);
  const viewRef = useRef(null);
  const waterfallRef = useRef(null);
  const waterfallViewRef = useRef(null);
  const [selectedDay, setSelectedDay] = useState(null);

  const series = useMemo(() => tracker?.series || [], [tracker]);
  const windowSeries = useMemo(() => tracker?.windowSeries || [], [tracker]);
  const maxDay = tracker?.windowDayCount || 0;

  useEffect(() => {
    setSelectedDay((prev) => (prev != null && prev <= maxDay ? prev : maxDay || null));
  }, [maxDay]);

  const eay = tracker?.perAcre?.eay ?? null;
  const fixedAcre = tracker?.perAcre?.fixedAcre ?? null;
  const perAcre = Boolean(tracker?.perAcre?.eayAvailable);
  const unit = perAcre ? "$/ac" : "$/bu";
  const places = perAcre ? 2 : 4;
  const priceSeriesLabel = perAcre ? "Projected revenue" : "Projected price";
  const cropBarLabel = perAcre ? "Crop revenue" : "Crop price";
  const priceCaption = perAcre ? "revenue" : "price";
  const trigger = perAcre ? (tracker?.perAcre?.triggerPerAcre ?? null) : (tracker?.perBushel?.triggerSpreadBu ?? null);

  // "As of" is the newest row in the series, not today's date — the series is sorted ascending.
  // The period itself comes from the reinsurance year (it sits in the prior calendar year), so
  // without that year there is no window to draw.
  const asOfDate = series.length ? series[series.length - 1].date : null;
  const reinsuranceYear = tracker?.reinsuranceYear ?? null;
  const win = useMemo(
    () => (asOfDate && reinsuranceYear ? discoveryWindow(reinsuranceYear, asOfDate) : null),
    [reinsuranceYear, asOfDate]
  );

  // The market-implied margin is the only series that exists all year: proj_* rows start on the
  // day the window opens, so without it the pre-window view is an empty chart.
  const showMarketMargin = Boolean(win && !win.periodOpen);

  const rows = useMemo(() => {
    if (!win) return [];
    // The county-yield bridge, applied to every series so price - cost = margin still holds on one
    // axis: margin through the named bridge formula, price as EAY x price, cost as the remainder.
    const asPrice = (v) => (perAcre ? revenuePerAcre(v, eay) : v);
    const asMargin = (v) => (perAcre ? bridgeToPerAcre(v, fixedAcre, eay) : v);
    const asCost = (v) => (perAcre ? costPerAcre(v, fixedAcre, eay) : v);

    return series
      .filter((r) => r.date && withinWindow(win, r.date))
      .map((r) => ({
        date: r.date,
        day: r.day,
        // proj_* is only meaningful on in-window rows (day number non-null).
        price: r.day != null ? asPrice(r.projPriceBu) : null,
        margin: r.day != null ? asMargin(r.projMarginBu) : null,
        cost: r.day != null ? asCost(r.projCostBu) : null,
        // Per-bushel values are exact and yield-free — keep them for the tooltip even when the
        // chart is in dollars per acre, so the estimate and the exact figure sit side by side.
        marginBu: r.day != null ? r.projMarginBu : null,
        priceBu: r.day != null ? r.projPriceBu : null,
        costBu: r.day != null ? r.projCostBu : null,
        marketMargin: showMarketMargin ? asMargin(r.mktMarginBu) : null,
      }))
      .filter((r) => r.margin != null || r.price != null || r.marketMargin != null);
  }, [series, win, fixedAcre, eay, perAcre, showMarketMargin]);

  const present = (field) => rows.some((r) => r[field] != null);

  // The dashed run to the window's close. Two points — the newest settled day and the close — so
  // the dashes leave the solid line exactly where it ends and hold that value flat. There is no
  // third point to add: nothing in tracker_daily forecasts a day that hasn't settled.
  const projectedRows = useMemo(() => {
    const last = rows.length ? rows[rows.length - 1] : null;
    if (!win?.periodOpen || !last) return [];
    const closeDate = new Date(win.periodEnd).toLocaleDateString("en-CA");
    return closeDate > last.date ? [last, { ...last, date: closeDate }] : [];
  }, [rows, win]);

  // The axis is zoomed to the three that share a level — price, margin and the trigger. Input cost
  // is deliberately NOT in the domain: per acre it runs near $200 against a $1,000 price, and
  // stretching the scale to reach it flattens the ~$16/ac drift this chart exists to show into a
  // few pixels. Per BUSHEL the three sit within cents of each other, so cost fits the zoom on its
  // own and draws; the test below decides that per render rather than per unit mode.
  const { yDomain, costInView } = useMemo(() => {
    const domain = paddedDomain([...rows.flatMap((r) => [r.price, r.margin, r.marketMargin]), trigger]);
    return {
      yDomain: domain,
      costInView:
        Boolean(domain) &&
        rows.some((r) => r.cost != null) &&
        rows.every((r) => r.cost == null || (r.cost >= domain[0] && r.cost <= domain[1])),
    };
  }, [rows, trigger]);

  // The scrubbed day, as plotted. Falls back to the newest settled day so the labels are never
  // blank while the slider is initializing.
  const selectedRow = useMemo(
    () => (rows.length ? rows.find((r) => r.day === (selectedDay ?? maxDay)) || rows[rows.length - 1] : null),
    [rows, selectedDay, maxDay]
  );

  const vegaSpec = useMemo(() => {
    if (!rows.length || !win) return null;
    const textColor = theme.palette.text.primary;

    const xEnc = { field: "date", type: "temporal", title: null, scale: { domain: [win.start, win.end] } };
    const yEnc = (field) => ({ field, type: "quantitative", title: unit, scale: { domain: yDomain } });
    const dateTip = { field: "date", type: "temporal", title: "Date" };
    const money = `$,.${places}f`;
    // The exact per-bushel figure alongside the plotted estimate, but only when they differ.
    const exactTip = (field, label) => (perAcre ? [{ field, title: `${label} /bu`, format: "$,.4f" }] : []);

    const lineLayer = (field, tooltip, mark, values) => ({
      ...(values ? { data: { values } } : {}),
      transform: [{ filter: `datum.${field} != null` }],
      mark: { type: "line", clip: true, ...mark },
      encoding: { x: xEnc, y: yEnc(field), tooltip },
    });

    const layer = [];
    // The input cost, drawn as the gap it actually is: the band from the margin line up to the
    // price line. First layer, so every line sits on top of it. `stack: null` is load-bearing —
    // an area's y stacks by default, which would stand the band on the axis floor and add the two
    // series together instead of spanning between them.
    if (present("price") && present("margin")) {
      layer.push({
        transform: [{ filter: "datum.price != null && datum.margin != null" }],
        mark: { type: "area", clip: true, fill: COLOR_BAND, line: false },
        encoding: {
          x: xEnc,
          y: { ...yEnc("margin"), stack: null },
          y2: { field: "price" },
          tooltip: [dateTip, { field: "cost", title: "Input costs", format: money }],
        },
      });
    }
    // Price, then cost, then margin: the hero line draws last so it sits on top of the crossings.
    [
      ["price", priceSeriesLabel, COLOR_PRICE, {}, "priceBu", "Price"],
      ["cost", "Input cost index", COLOR_COST, {}, "costBu", "Input costs"],
      [
        "margin",
        "Projected margin",
        COLOR_MARGIN,
        // The point overlay does NOT inherit the line's color — without it the per-day dots render
        // in Vega's default blue on top of the green margin line.
        { strokeWidth: 5, point: { size: 90, filled: true, color: COLOR_MARGIN } },
        "marginBu",
        "Margin",
      ],
    ].forEach(([field, label, color, extraMark, buField, buLabel]) => {
      if (!present(field) || (field === "cost" && !costInView)) return;
      const mark = { color, ...extraMark };
      layer.push(
        lineLayer(field, [dateTip, { field, title: label, format: money }, ...exactTip(buField, buLabel)], mark)
      );
      if (projectedRows.length) {
        layer.push(
          lineLayer(
            field,
            [dateTip, { field, title: `${label} (held to close)`, format: money }],
            { ...mark, strokeDash: [5, 4], point: false },
            projectedRows
          )
        );
      }
    });
    if (present("marketMargin")) {
      layer.push(
        lineLayer("marketMargin", [dateTip, { field: "marketMargin", title: "Market-implied margin", format: money }], {
          color: COLOR_MARKET,
          strokeDash: [4, 3],
        })
      );
    }
    // No x/x2 on the rule on purpose: a rule with only y spans the view's full width, which is
    // what a reference line needs. Giving it the date-keyed x encoding (its one-row data has no
    // date field) silently drops the mark instead of drawing it.
    if (trigger != null) {
      layer.push({
        data: { values: [{ value: trigger }] },
        mark: { type: "rule", strokeDash: [8, 5], color: COLOR_TRIGGER },
        encoding: { y: yEnc("value") },
      });
    }

    // The scrubbed day: a dashed spine, a dot on each line, and the three values written beside
    // them. Past ~3/4 of the way across, the labels flip to the left of the spine so they stay
    // inside the plot instead of being clipped at the right edge.
    if (selectedRow?.price != null && selectedRow?.margin != null) {
      const flip = rows.indexOf(selectedRow) > rows.length * 0.72;
      const align = flip ? "right" : "left";
      const dx = flip ? -12 : 12;
      const marker = [{ ...selectedRow, costMid: (selectedRow.price + selectedRow.margin) / 2 }];
      const markerLayer = (mark, encoding) => layer.push({ data: { values: marker }, mark, encoding });

      markerLayer({ type: "rule", clip: true, strokeDash: [3, 3], strokeWidth: 1, color: COLOR_GUIDE }, { x: xEnc });
      [
        ["price", COLOR_PRICE],
        ["margin", COLOR_MARGIN],
      ].forEach(([field, color]) =>
        markerLayer({ type: "point", clip: true, filled: true, size: 90, color }, { x: xEnc, y: yEnc(field) })
      );
      [
        ["price", priceCaption, COLOR_PRICE, -12, 700],
        ["costMid", "costs", COLOR_COST, 0, 400],
        ["margin", "margin", COLOR_MARGIN, 16, 700],
      ].forEach(([field, caption, color, dy, fontWeight]) => {
        // The cost label reads the gap, not a plotted series — its text comes from `cost`, which is
        // the same value whether or not the cost line itself made it into the zoomed domain.
        const value = caption === "costs" ? selectedRow.cost : selectedRow[field];
        if (value == null) return;
        markerLayer(
          { type: "text", clip: true, align, dx, dy, fontSize: 11, fontWeight, color },
          { x: xEnc, y: yEnc(field), text: { value: `${caption} ${f2(value)}` } }
        );
      });
    }

    return {
      $schema: "https://vega.github.io/schema/vega-lite/v6.json",
      config: {
        background: "transparent",
        view: { stroke: null },
        axis: { labelColor: textColor, titleColor: textColor, labelFontSize: 12, titleFontSize: 13 },
        // Horizontal gridlines only. A vertical line per trading day fights the dashed spine that
        // marks the scrubbed day, and the dates are already read off the axis, not off the surface.
        axisX: { grid: false },
        // Stroke weight lives here, not per layer: the hero margin line overrides it, everything
        // else inherits, so "make the lines thicker" is one number.
        line: { strokeWidth: 3.5, strokeCap: "round" },
        rule: { strokeWidth: 3 },
      },
      title: {
        text: projectedRows.length
          ? "Discovery window: settled to date (solid) vs. projected to close (dashed)"
          : "Discovery window: settled to date",
        // An open window's dates read straight off the x axis; only the pre-window view, whose
        // axis is a rolling lookback rather than the period itself, still has to explain them.
        ...(win.periodOpen
          ? {}
          : {
              subtitle: `Last ${Math.round((win.end - win.start) / 86400000)} days - discovery period opens ${formatMs(win.periodStart)}`,
            }),
        color: textColor,
        subtitleColor: theme.palette.text.secondary,
        fontSize: 13,
        subtitleFontSize: 11,
        anchor: "start",
      },
      data: { values: rows },
      width: "container",
      height: 260,
      layer,
    };
  }, [rows, projectedRows, selectedRow, win, trigger, yDomain, costInView, unit, places, perAcre, theme, priceSeriesLabel, priceCaption]);

  // The same day the scrubber selects, as a subtraction: the crop price bar, the input cost as a
  // floating segment hanging off its top, and the margin bar that is what's left. Dashed guides
  // carry the price and margin levels across so the middle bar reads as the difference.
  const waterfallSpec = useMemo(() => {
    const row = selectedRow;
    if (!row || row.price == null || row.margin == null || row.cost == null) return null;
    const marginLabel = `Margin per ${perAcre ? "acre" : "bushel"}`;
    const order = [cropBarLabel, "Input costs", marginLabel];

    // Zero-based, so the cost reads as the share of the price it actually is. Headroom scales off
    // the cost itself, which keeps the geometry identical per bushel (cents) and per acre
    // (hundreds of dollars) without a second set of constants.
    const spread = Math.max(row.price - row.margin, Math.abs(row.price) * 0.02) || 1;
    const base = Math.min(0, row.margin);
    const yScale = { domain: [base, row.price + spread * 0.5] };
    const xEnc = { field: "label", type: "nominal", title: null, sort: order, axis: { labelAngle: 0 } };

    return {
      $schema: "https://vega.github.io/schema/vega-lite/v6.json",
      config: {
        background: "transparent",
        view: { stroke: null },
        axis: {
          labelColor: theme.palette.text.primary,
          titleColor: theme.palette.text.primary,
          labelFontSize: 12,
          titleFontSize: 13,
        },
      },
      width: "container",
      height: 280,
      layer: [
        {
          data: {
            values: [
              { label: cropBarLabel, lo: base, hi: row.price },
              { label: "Input costs", lo: row.margin, hi: row.price },
              { label: marginLabel, lo: base, hi: row.margin },
            ],
          },
          mark: { type: "bar", cornerRadiusEnd: 3, size: 104 },
          encoding: {
            x: xEnc,
            y: { field: "lo", type: "quantitative", title: unit, scale: yScale },
            y2: { field: "hi" },
            color: {
              field: "label",
              type: "nominal",
              legend: null,
              scale: { domain: order, range: [COLOR_PRICE, COLOR_COST, COLOR_MARGIN] },
            },
            tooltip: [
              { field: "label", title: "Bar" },
              { field: "hi", title: "Top", format: `$,.${places}f` },
              { field: "lo", title: "Bottom", format: `$,.${places}f` },
            ],
          },
        },
        {
          data: { values: [{ value: row.price }, { value: row.margin }] },
          mark: { type: "rule", strokeDash: [3, 3], strokeWidth: 1, color: COLOR_GUIDE },
          encoding: { y: { field: "value", type: "quantitative", scale: yScale, title: null } },
        },
        {
          data: {
            values: [
              { label: cropBarLabel, value: row.price, text: f2(row.price) },
              { label: marginLabel, value: row.margin, text: f2(row.margin) },
            ],
          },
          mark: { type: "text", dy: -10, fontSize: 15, fontWeight: 600, color: theme.palette.text.primary },
          encoding: {
            x: xEnc,
            y: { field: "value", type: "quantitative", scale: yScale, title: null },
            text: { field: "text" },
          },
        },
        {
          data: { values: [{ label: "Input costs", value: (row.price + row.margin) / 2 }] },
          mark: {
            type: "text",
            align: "left",
            dx: 60,
            fontSize: 14,
            fontWeight: 600,
            color: theme.palette.text.primary,
          },
          encoding: {
            x: xEnc,
            y: { field: "value", type: "quantitative", scale: yScale, title: null },
            text: { value: `−${f2(row.cost)}` },
          },
        },
      ],
    };
  }, [selectedRow, perAcre, unit, places, theme, cropBarLabel]);

  useEffect(() => {
    if (viewRef.current) {
      viewRef.current.finalize();
      viewRef.current = null;
    }
    if (!chartRef.current || !vegaSpec) return;
    vegaEmbed(chartRef.current, vegaSpec, { actions: false, renderer: "svg" })
      .then((result) => {
        viewRef.current = result.view;
      })
      .catch((error) => console.error("Error rendering MCO tracker chart:", error));
    return () => {
      if (viewRef.current) {
        viewRef.current.finalize();
        viewRef.current = null;
      }
    };
  }, [vegaSpec]);

  useEffect(() => {
    if (waterfallViewRef.current) {
      waterfallViewRef.current.finalize();
      waterfallViewRef.current = null;
    }
    if (!waterfallRef.current || !waterfallSpec) return;
    vegaEmbed(waterfallRef.current, waterfallSpec, { actions: false, renderer: "svg" })
      .then((result) => {
        waterfallViewRef.current = result.view;
      })
      .catch((error) => console.error("Error rendering MCO margin waterfall:", error));
    return () => {
      if (waterfallViewRef.current) {
        waterfallViewRef.current.finalize();
        waterfallViewRef.current = null;
      }
    };
  }, [waterfallSpec]);

  if (!series.length) return null;

  const legend = [
    present("margin") && [COLOR_MARGIN, "solid", "Projected margin"],
    trigger != null && [COLOR_TRIGGER, "dashed", "Trigger margin"],
    present("price") && [COLOR_PRICE, "solid", priceSeriesLabel],
    costInView && present("cost") && [COLOR_COST, "solid", "Input cost index"],
    present("marketMargin") && [COLOR_MARKET, "dashed", "Market-implied margin"],
  ].filter(Boolean);

  const windowClosed = Boolean(win) && daysToClose(win) === 0;
  // A closed window always shows its final day. With no scrubber there is nothing to clear a
  // selection that outlived the window closing mid-session, so it is pinned rather than stranded.
  const day = windowClosed ? maxDay : (selectedDay ?? maxDay);
  const breakdownRow = windowSeries.find((r) => r.day === day) || null;
  // Day one is the drift baseline: every "since day 1" on this screen compares against it.
  const baselineRow = windowSeries.length ? windowSeries[0] : null;
  // A settled window's basis comes off the is_latest row, which sits OUTSIDE the window; the
  // in-window rows all still read "forming", which is what an open window should show.
  const basisPhrase = BASIS_PHRASE[windowClosed ? tracker?.basis : breakdownRow?.projBasis] || null;

  // The per-leg split, cent-allocated so the legs on screen add up to the total on screen.
  const basketLegs = (tracker?.basket || []).map((b) => b.leg);
  // Gated on the day's own cost total as well as the split: the legs are only shown as a
  // decomposition OF that total, and every consumer below divides or allocates against it.
  const splitLegs =
    tracker?.costSplitReady && breakdownRow?.projCostBu != null
      ? LEG_ORDER.filter((leg) => breakdownRow.contribs?.[leg] != null)
      : [];
  const shownCents = splitLegs.length
    ? allocateCents(
        splitLegs.map((leg) => breakdownRow.contribs[leg]),
        breakdownRow.projCostBu
      )
    : null;

  // Ledger: one line per contract behind the guarantee. Legs outside the commodity's basket are
  // still listed when the mirror carries a price for them, but they contribute nothing.
  const symbolFor = (legName) => priceLegs.find((l) => l.leg === legName)?.symbol || "";
  const ledgerLegs = LEG_ORDER.filter((leg) => basketLegs.includes(leg) || breakdownRow?.legs?.[leg] != null);

  // Concept-preview stat cards: where the window stands right now. A null value drops its card
  // rather than printing "$0" for something RMA hasn't determined.
  const settledRow = windowSeries.length ? windowSeries[windowSeries.length - 1] : null;
  const remaining = win ? daysToClose(win) : null;
  // Through the newest SETTLED day, not the newest row in `series` — that one sits outside the
  // window and would date a "to date" figure months past the window it summarizes.
  const throughNote = win ? `${formatMs(win.periodStart)} - ${formatDateShort(settledRow?.date)}` : "";
  const stats = [
    [
      "Proj. price (to date)",
      // The RMA projected price itself, per bushel — the figure an agent quotes. Deliberately not
      // bridged through the yield like the chart is: x EAY makes it revenue, not a price.
      settledRow?.projPriceBu != null ? formatPrice(settledRow.projPriceBu, 4) : null,
      `per bushel · ${throughNote}`,
    ],
    [
      "Input cost index",
      settledRow?.projCostBu != null
        ? formatPrice(perAcre ? costPerAcre(settledRow.projCostBu, fixedAcre, eay) : settledRow.projCostBu, places)
        : null,
      `fertilizer + energy${perAcre ? " per acre" : " per bushel"}`,
    ],
    [
      "Days to close",
      win ? String(remaining) : null,
      win && remaining > 0 ? `until ${formatMs(win.periodEnd)}` : `locked ${formatMs(win?.periodEnd)}`,
    ],
    [
      "Forecast payment",
      forecastPaymentPerAcre != null ? `${formatPrice(forecastPaymentPerAcre, 2)}/ac` : null,
      "at projected price & expected yield",
    ],
  ].filter(([, value]) => value != null);

  return (
    <Box sx={{ mb: 1 }}>
      {tracker?.harvestState && (
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <Chip size="small" label={`Harvest: ${BASIS_LABEL[tracker.harvestState] || tracker.harvestState}`} />
        </Stack>
      )}

      {Boolean(stats.length) && (
        <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 2, rowGap: 2 }}>
          {stats.map(([label, value, note]) => {
            // The payment is the number the whole screen is building toward — it gets the one
            // accent card so it reads before the three inputs that produce it.
            const accent = label === "Forecast payment";
            return (
              <Card
                key={label}
                variant="outlined"
                sx={{ p: 1.5, minWidth: 160, ...(accent && { bgcolor: "#fdecd8", borderColor: "#f0b47a" }) }}
              >
                <Typography
                  variant="caption"
                  color={accent ? "#a8551a" : "text.secondary"}
                  sx={{ display: "block", textTransform: "uppercase" }}
                >
                  {label}
                </Typography>
                <Typography variant="h6" sx={accent ? { color: "#c2621b", fontWeight: 700 } : undefined}>
                  {value}
                </Typography>
                <Typography variant="caption" color={accent ? "#a8551a" : "text.secondary"} sx={{ display: "block" }}>
                  {note}
                </Typography>
              </Card>
            );
          })}
        </Stack>
      )}

      <Card variant="outlined" sx={{ p: 2 }}>
        <div ref={chartRef} style={{ width: "100%" }} />

        {Boolean(legend.length) && (
          <Stack direction="row" spacing={2.5} flexWrap="wrap" sx={{ mt: 1, rowGap: 1 }}>
            {legend.map(([color, style, label]) => (
              <Stack key={label} direction="row" spacing={1} alignItems="center">
                <Box sx={{ width: 26, borderTop: `4px ${style} ${color}`, flexShrink: 0 }} />
                <Typography variant="body2" color="text.primary">
                  {label}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </Card>

      {!vegaSpec && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          No settled days in this window yet.
        </Typography>
      )}

      {Boolean(maxDay) && (
        <Box sx={{ mt: 2, maxWidth: 500 }}>
          <Typography variant="body2" gutterBottom>
            Trading day {day} of {maxDay}
            {windowClosed && " — window closed"}
          </Typography>
          {/* The scrubber is a progress indicator for a window still filling. Once the window has
              closed there is no progress left to show and nothing can move the guarantee, so the
              screen holds the final day rather than inviting a scrub through settled history. */}
          {!windowClosed && (
            <Slider min={1} max={maxDay} value={day} onChange={(_, v) => setSelectedDay(v)} valueLabelDisplay="auto" />
          )}
        </Box>
      )}

      {/* Is the guarantee still forming, or is it set? The one question this screen exists to
          answer, stated in words rather than left to be inferred from the scrubber's position. */}
      {breakdownRow && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.7, maxWidth: 760 }}>
          {windowClosed ? (
            <>
              <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
                All {maxDay} trading days are counted — the window closed {breakdownRow.date}.
              </Box>{" "}
              This guarantee is set; every value is the average that counts toward it.
              {tracker?.officialPrice != null &&
                ` RMA's official price is ${formatPrice(tracker.officialPrice, 2)}; our tracked average landed at ${f2(
                  breakdownRow.projPriceBu
                )}.`}
            </>
          ) : (
            <>
              <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
                {day} of ~{maxDay} days counted ({breakdownRow.date}).
              </Box>{" "}
              {Math.round((day / maxDay) * 100)}% of the guarantee is permanent, and the remaining days can still move
              it. Every value is the running average of settled days — exactly what counts toward the guarantee.
            </>
          )}
          {basisPhrase && <Chip size="small" label={basisPhrase} sx={{ ml: 1, verticalAlign: "middle" }} />}
          {breakdownRow.potashStatus && breakdownRow.potashStatus !== "ok" && (
            <Chip size="small" color="warning" label={`potash: ${breakdownRow.potashStatus}`} sx={{ ml: 1 }} />
          )}
        </Typography>
      )}

      {/* Why the whole screen is per bushel, in the agent's words. */}
      {!perAcre && breakdownRow && (
        <Alert severity="info" variant="outlined" sx={{ mt: 2, maxWidth: 760 }}>
          Everything here is per bushel. Dollars per acre need your county&apos;s expected yield, and RMA doesn&apos;t
          publish that until a few days after this window closes. Per bushel needs no yield, so it is exact from day
          one. Once RMA publishes the yield, the two combine: margin per bushel × county yield, minus diesel per acre.
        </Alert>
      )}

      {waterfallSpec && (
        <Card variant="outlined" sx={{ p: 2, mt: 2 }}>
          <div ref={waterfallRef} style={{ width: "100%" }} />
          {rows[0] && selectedRow && (
            <Stack direction="row" sx={{ mt: 0.5 }}>
              {[
                drift(selectedRow.price, rows[0].price),
                drift(selectedRow.cost, rows[0].cost),
                drift(selectedRow.margin, rows[0].margin),
              ].map((text, i) => (
                <Typography key={i} variant="caption" color="text.secondary" sx={{ flex: 1, textAlign: "center" }}>
                  {text && `${text} since day 1`}
                </Typography>
              ))}
            </Stack>
          )}
        </Card>
      )}

      {Boolean(splitLegs.length) && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            What makes up the input costs, per bushel — value, then change since day 1
          </Typography>
          <Stack direction="row" flexWrap="wrap" sx={{ columnGap: 3, rowGap: 1, mb: 1.5 }}>
            {splitLegs.map((leg, i) => (
              <Stack key={leg} direction="row" spacing={1} alignItems="center" sx={{ minWidth: 210 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: "2px", bgcolor: LEG_COLOR[leg], flexShrink: 0 }} />
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                  {LEG_META[leg][0]}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {f2(shownCents ? shownCents[i] : breakdownRow.contribs[leg])}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 44, textAlign: "right" }}>
                  {drift(breakdownRow.contribs[leg], baselineRow?.contribs?.[leg])}
                </Typography>
              </Stack>
            ))}
          </Stack>
          {/* Segment widths follow the ALLOCATED value, so the strip and the legend can never
              disagree about which leg is the biggest. */}
          <Stack direction="row" sx={{ height: 24, borderRadius: 1, overflow: "hidden" }}>
            {splitLegs.map((leg, i) => {
              const value = shownCents ? shownCents[i] : breakdownRow.contribs[leg];
              return (
                <Tooltip key={leg} title={`${LEG_META[leg][0]} ${f2(value)}`} arrow>
                  <Box
                    sx={{
                      width: `${(value / breakdownRow.projCostBu) * 100}%`,
                      bgcolor: LEG_COLOR[leg],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 0,
                      overflow: "hidden",
                    }}
                  >
                    {/* Every segment states its own value. The strip runs the full dialog width,
                        so even the smallest leg has room; overflow:hidden is the backstop, and the
                        tooltip names the leg either way. */}
                    <Typography variant="caption" noWrap sx={{ fontWeight: 600, color: "#3d2a14" }}>
                      {f2(value)}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Stack>
        </Box>
      )}

      {tracker?.basket?.length > 0 && !tracker?.costSplitReady && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
          Component breakdown withheld: the per-leg split differs from the tracker&apos;s own input cost by up to{" "}
          {formatPrice(tracker.costSplitGap, 4)}/bu, so the legs would not add up to the total.
        </Typography>
      )}

      {breakdownRow && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Contract ledger — {formatDateShort(breakdownRow.date)}
          </Typography>
          <Table size="small" className="reportTable" sx={{ maxWidth: 760 }}>
            <TableHead>
              <TableRow>
                <TableCell>Leg</TableCell>
                <TableCell>Contract</TableCell>
                <TableCell align="right">Running average</TableCell>
                <TableCell align="right">Per bushel</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Crop</TableCell>
                <TableCell>{symbolFor("Crop") || "—"}</TableCell>
                <TableCell align="right">{priceOrPending(breakdownRow.projPriceBu, 2)}/bu</TableCell>
                <TableCell align="right">{priceOrPending(breakdownRow.projPriceBu, 2)}</TableCell>
              </TableRow>
              {ledgerLegs.map((leg) => {
                const [name, officialName, nativeUnit] = LEG_META[leg];
                const symbol = symbolFor(officialName);
                const contribution = splitLegs.includes(leg)
                  ? shownCents[splitLegs.indexOf(leg)]
                  : breakdownRow.contribs?.[leg];
                return (
                  <TableRow key={leg}>
                    <TableCell>
                      {name.replace(", per bushel", "")}
                      {/* Diesel is the one leg with two parts: a per-bushel share in the column
                          opposite, plus a flat per-acre charge that no yield scales. */}
                      {leg === "diesel" && breakdownRow.projFixedAcre != null && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          plus {f2(breakdownRow.projFixedAcre)}/ac flat
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {symbol || (
                        <Typography variant="caption" color="text.secondary" fontStyle="italic">
                          no contract — priced from a published formula
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {breakdownRow.legs?.[leg] != null
                        ? `${formatPrice(breakdownRow.legs[leg], 2)}${nativeUnit}`
                        : "—"}
                    </TableCell>
                    <TableCell align="right">
                      {contribution != null ? (
                        `−${f2(contribution)}`
                      ) : leg === "naturalGas" && !basketLegs.includes(leg) ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", maxWidth: 160 }}>
                          N/A - Non-Irrigated
                        </Typography>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, maxWidth: 760 }}>
            Fertilizer months are the contracts the rules pin. When a pinned month has no trades in the window, the
            average comes from the nearest month that does.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
